'use client'
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon'
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

interface IAnnouncementsPaginationProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}

export function AnnouncementsPagination({
  page,
  totalPages,
  onPageChange
}: IAnnouncementsPaginationProps) {
  const t = useTranslations('UsefulInformation')

  if (totalPages <= 1) return null

  const pages = Array.from({ length: totalPages }, (_, index) => index + 1)
  const arrowClasses =
    'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent'

  return (
    <nav
      aria-label={t('tabs.announcements.pagination.label')}
      className="flex items-center justify-center gap-2 pt-4"
    >
      <button
        type="button"
        aria-label={t('tabs.announcements.pagination.previous')}
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        className={arrowClasses}
      >
        <ChevronLeftIcon />
      </button>
      {pages.map((pageNumber) => (
        <button
          key={pageNumber}
          type="button"
          aria-label={t('tabs.announcements.pagination.page', {
            page: pageNumber
          })}
          aria-current={pageNumber === page ? 'page' : undefined}
          onClick={() => onPageChange(pageNumber)}
          className={cn(
            'text-body-3 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors',
            pageNumber === page
              ? 'bg-primary-500 text-white'
              : 'text-primary-600 hover:bg-gray-100'
          )}
        >
          {pageNumber}
        </button>
      ))}
      <button
        type="button"
        aria-label={t('tabs.announcements.pagination.next')}
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        className={arrowClasses}
      >
        <ChevronRightIcon />
      </button>
    </nav>
  )
}
